import { Button as MUIButton, Typography, Checkbox } from '@material-ui/core/';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import debounce from 'lodash/debounce';
import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useContext,
  createContext
} from 'react';
import { sortBy } from 'lodash';
import {
  Button,
  Datagrid,
  Filter,
  FunctionField,
  TextInput,
  Show,
  TextField,
  Toolbar,
  TopToolbar,
  useQuery,
  Loading,
  useNotify,
  resolveRedirectTo,
  SaveButton,
  useRefresh
} from 'react-admin';
import FormTitleBar from '../../core/components/FormTitleBar';
import { getInitials } from '../../core/utils';
import EmailDialog from './createsteps/EmailDialog';
import PopupField from '../../core/components/grid/PopupField';
import DistributionListApi from '../../core/apis/DistributionListApi';
import { saveAs } from 'file-saver';
import FormHelperText from '@material-ui/core/FormHelperText';
import Paper from '@material-ui/core/Paper';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitleWithCloseButton from '../../core/components/DialogTitleWithCloseButton';
import { difference } from 'lodash';
import { splitAndTrimString } from '../../core/utils/index';
import { TextField as MUITextField } from '@material-ui/core';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';

/* MUI styles */
const styles = theme => ({
  root: {
    marginLeft: '28px',
    marginRight: '28px'
  },
  toolbar: {
    justifyContent: 'space-between',
    alignItems: 'inherit',
    backgroundColor: 'transparent',
    marginTop: 0,
    paddingRight: 0,
    [theme.breakpoints.up('xs')]: {
      paddingLeft: 0
    },
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(2),
      backgroundColor: theme.palette.background.paper
    }
  },
  composeEmailButton: {
    marginLeft: '10px',
    border: '1px solid #E0E0E0',
    background: 'white',
    alignSelf: 'center'
  },
  actionBarContainer: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  indicatorLabel: {
    fontSize: 14,
    color: '#757575',
    paddingRight: '18px'
  },
  indicatorNumber: {
    fontSize: 14,
    color: '#212121'
  },
  loading: {
    width: '100%',
    height: '100%',
    alignSelf: 'center'
  },
  helperSearchInput: {
    fontSize: '0.75rem',
    fontFamily: '"Segoe UI",Arial,sans-serif',
    color: 'rgba(0, 0, 0, 0.54)',
    fontWeight: '400',
    lineHeight: '1.66'
  },
  notSelectedUsersIndicatorLabel: {
    fontSize: 14,
    color: '#2962FF'
  },
  paperContainer: {
    width: '100%'
  },
  table: {
    overflow: 'auto',
    maxHeight: 'calc(100vh - 364px)',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px'
    },
    /* Track */
    '&::-webkit-scrollbar-track': {
      background: '#ebebeb',
      borderRadius: '10px',
      margin: '2px'
    },
    /* Handle */
    '&::-webkit-scrollbar-thumb': {
      background: '#9E9E9E',
      borderRadius: '10px'
    },
    /* Handle on hover */
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#7b7a7a'
    }
  },
  tableContainer: {
    borderTop: '0px'
  },
  checkboxCell: {
    width: '24px',
    borderLeft: '0',
    borderRight: '1px solid rgba(224, 224, 224, 1)'
  },
  cell: {
    borderLeft: '0',
    '&:last-child': {
      borderRight: '0'
    }
  },
  excludedContactsLabel: {
    fontSize: 14,
    alignSelf: 'center',
    color: '#2962FF',
    paddingRight: '18px',
    '&:hover': {
      cursor: 'pointer'
    }
  },
  searchTextInput: {
    marginTop: '25px',
    marginBottom: '10px'
  },
  checkbox: {
    padding: '0'
  }
});

/**
 * Screen title
 */
const DistributionListTitle = ({ record }) => {
  return (
    <FormTitleBar
      name={record.name}
      showAvatar
      avatarContent={getInitials(record.name)}
    />
  );
};

/**
 * Recipients list filter
 */
const RecipientsFilters = props => {
  return (
    <Filter
      variant="standard"
      {...props}
      filterValues={{ email: '', contact: '', fundName: '', investors: '' }}
      setFilters={props.setFilters}
      hideFilter={() => {}}
      resource="distributionlistrecipients"
    >
      <TextInput
        source="email"
        alwaysOn
        label="Search by Email"
        options={{ autoComplete: 'off' }}
      />
      <TextInput
        source="contact"
        alwaysOn
        label="Search by Contact"
        options={{ autoComplete: 'off' }}
      />
      <TextInput
        source="fundName"
        alwaysOn
        label="Search by Fund"
        options={{ autoComplete: 'off' }}
      />
      <TextInput
        source="investors"
        alwaysOn
        label="Search by Investor"
        options={{ autoComplete: 'off' }}
      />
    </Filter>
  );
};

/**
 * RecipientsList actions
 */
const RecipientsActions = ({
  classes,
  selectedIds,
  onCompose,
  ids,
  onDownloadToExcel,
  isLoading,
  hasComposeEmailDLPermission,
  hasExportPermission,
  setExcludedContactsDialogOpen,
  numberOfExcludedContacts
}) => (
  <TopToolbar className={classes.toolbar}>
    <span
      className={classes.excludedContactsLabel}
      onClick={
        numberOfExcludedContacts > 0
          ? () => setExcludedContactsDialogOpen(true)
          : null
      }
      disabled={numberOfExcludedContacts === 0}
      style={{ color: numberOfExcludedContacts === 0 ? 'black' : '#2962FF' }}
    >
      Add Excluded Contacts
    </span>
    {hasExportPermission && (
      <Button
        color="primary"
        label="Download as Excel"
        className={classes.composeEmailButton}
        onClick={isLoading ? null : onDownloadToExcel}
        disabled={(ids && ids.length < 1) || isLoading}
      />
    )}
    {hasComposeEmailDLPermission && (
      <Button
        color="primary"
        label="Compose Email"
        className={classes.composeEmailButton}
        onClick={onCompose}
        disabled={selectedIds && selectedIds.length < 1}
        disableFocusRipple
      />
    )}
  </TopToolbar>
);

const calculateSelectedCCAll = users => {
  const ccCount = users.reduce(
    (accu, user) => [
      accu[0] +
        (user.isSelected && user.ccEmails && user.ccEmails.length ? 1 : 0),
      accu[1] +
        (user.isSelected &&
        user.ccEmails &&
        user.ccEmails.length &&
        user.isSelectedCC
          ? 1
          : 0)
    ],
    [0, 0]
  );
  return ccCount[0] === ccCount[1] ? ccCount[0] !== 0 : false;
};

const CkAllContext = createContext({ checked: true });

const CkAllLabel = ({ label, onChange }) => {
  const { checked } = useContext(CkAllContext);
  return (
    <label>
      <Checkbox checked={checked} onChange={onChange} />
      {label}
    </label>
  );
};
/**
 * List of recipients
 */
const RecipientsGrid = ({
  record,
  classes,
  data,
  ids = [],
  ccIds = [],
  recipients,
  handleFilters,
  hasComposeEmailDLPermission,
  hasExportPermission,
  userProfile,
  setFinalData,
  ...rest
}) => {
  // email dialog
  const [showEmaildDialog, setShowEmaildDialog] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notChanged, setNothingChanged] = useState(true);

  const checkedState = useContext(CkAllContext);

  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const refresh = useRefresh();

  const handleEmailDialogToggle = useCallback(toggle => {
    setShowEmaildDialog(toggle);
  }, []);

  //ids of visible recipients in grid
  const recipientsIds = useMemo(() => {
    return Object.keys(recipients);
  }, [recipients]);

  //selected ids of all recipients
  const selectedIds = useMemo(() => {
    const selectedUsers = data.users.filter(user => user.isSelected);
    return selectedUsers.map(user => `${user.userId}:${user.fundId}`);
  }, [data.users]);

  //selected ccIds of all recipients
  const selectedCCIds = useMemo(() => {
    const selectedCCUsers = data.users.filter(
      user =>
        user.isSelectedCC &&
        selectedIds.indexOf(`${user.userId}:${user.fundId}`) !== -1
    );
    return selectedCCUsers.map(user => `${user.userId}:${user.fundId}`);
  }, [data.users, selectedIds]);

  //selected Ids of filtered recipients visible in grid
  const gridSelectedIds = useMemo(() => {
    return selectedIds.filter(
      selectedId => recipientsIds.indexOf(selectedId) !== -1
    );
  }, [selectedIds, recipientsIds]);

  const [excludedContactsDialogOpen, setExcludedContactsDialogOpen] = useState(
    false
  );
  const [filteredExcludedContacts, setFilteredExcludedContacts] = useState([]);
  const [excludedContacts, setExcludedContacts] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [addDisabled, setAddDisabled] = useState(true);

  //save action of dialog for adding excluded contacts
  const addExcludedContacts = excludedContacts => {
    if (excludedContacts) {
      const addedContacts = excludedContacts.map(contact => ({
        ...contact,
        isSelected: true,
        isSelectedCC:
          Array.isArray(contact.ccEmails) && contact.ccEmails.length > 0
      }));
      let combinedUsers = [...data.users, ...addedContacts];
      let newFinalData = { ...data, users: combinedUsers };
      setFinalData(newFinalData);
      setNothingChanged(false);
    }
  };

  //effect used to fetch from api service list of excluded contacts
  useEffect(() => {
    if (data && data.users) {
      const paramNotSelected = {
        fundIds: [...new Set(data.users.map(item => item.fundId))],
        excludedUserFundRelations: data.users.map(item => {
          return {
            userId: item.userId,
            fundId: item.fundId
          };
        })
      };
      async function getExcludedContacts() {
        let responseList = await DistributionListApi.getExcludedContactsFromDistributionList(
          paramNotSelected
        );
        setExcludedContacts(responseList);
        setFilteredExcludedContacts(responseList);
      }

      // Execute the created function directly
      getExcludedContacts();
    }
  }, [data]);

  const filterExcludedContacts = event => {
    let excludedContactsFilter = splitAndTrimString(event.target.value, ',');

    let filteredList = excludedContacts.filter(item => {
      return (
        !excludedContactsFilter ||
        excludedContactsFilter.some(
          value =>
            item.firstName
              .concat(' ' + item.lastName)
              .toLowerCase()
              .includes(value === '' ? null : value.toLowerCase()) ||
            item.email
              .toLowerCase()
              .includes(value === '' ? null : value.toLowerCase()) ||
            item.fundName
              .toLowerCase()
              .includes(value === '' ? null : value.toLowerCase()) ||
            item.fundId
              .toLowerCase()
              .includes(value === '' ? null : value.toLowerCase())
        )
      );
    });

    setFilteredExcludedContacts(filteredList);
  };

  //callback used when single line checked is changed
  const handleToggleRecipient = useCallback(
    id => {
      setNothingChanged(false);
      const index = selectedIds.indexOf(id);
      const distributionListUsers = [...data.users];
      const changedUserIndex = distributionListUsers.findIndex(
        user => `${user.userId}:${user.fundId}` === id
      );
      if (index === -1) {
        distributionListUsers[changedUserIndex] = muteObject(
          distributionListUsers[changedUserIndex],
          {
            isSelected: true
          }
        );
      } else {
        distributionListUsers[changedUserIndex] = muteObject(
          distributionListUsers[changedUserIndex],
          {
            isSelected: false
          }
        );
        // Also remove selected CCs
        const ccIndex = selectedCCIds.indexOf(id);
        if (ccIndex >= 0) {
          distributionListUsers[changedUserIndex] = muteObject(
            distributionListUsers[changedUserIndex],
            {
              isSelectedCC: false
            }
          );
        }
      }
      checkedState.checked = calculateSelectedCCAll(distributionListUsers);
      setFinalData(muteObject(data, { users: distributionListUsers }));
    },
    [selectedIds, selectedCCIds, data, setFinalData, checkedState]
  );

  /**
   * Click on button 'Download as Excel' to download the excel file
   */
  const handleClickDownloadAsExcel = useCallback(
    id => {
      setLoading(true);
      DistributionListApi.getRecipientsExcelForDistList(id)
        .then(resp => {
          const fileName = 'distributionlist.xlsx';
          saveAs(resp, fileName);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          notify('ra.page.error', { type: 'error' });
        });
    },
    [notify]
  );

  /**
   * Close button will return us to Distribution Lists page
   */
  const handleClose = () => {
    rest.history.push(resolveRedirectTo('list', rest.basePath));
  };

  //callback used for grid checkbox selection changed (for all recipients)
  const handleSelectRecipients = useCallback(
    ids => {
      const distributionListUsers = [...data.users];
      if (ids.length === 0 && recipientsIds.length > 0) {
        recipientsIds.forEach(id => {
          const changedUserIndex = distributionListUsers.findIndex(
            user => `${user.userId}:${user.fundId}` === id
          );
          distributionListUsers[changedUserIndex] = muteObject(
            distributionListUsers[changedUserIndex],
            {
              isSelected: !distributionListUsers[changedUserIndex].isSelected
            }
          );

          if (distributionListUsers[changedUserIndex].isSelectedCC) {
            distributionListUsers[changedUserIndex] = muteObject(
              distributionListUsers[changedUserIndex],
              {
                isSelectedCC: distributionListUsers[changedUserIndex].isSelected
              }
            );
          }
        });
      } else {
        ids.forEach(id => {
          const changedUserIndex = distributionListUsers.findIndex(
            user => `${user.userId}:${user.fundId}` === id
          );
          distributionListUsers[changedUserIndex] = muteObject(
            distributionListUsers[changedUserIndex],
            {
              isSelected: true,
              isSelectedCC: true
            }
          );
        });
      }
      checkedState.checked = calculateSelectedCCAll(distributionListUsers);
      setNothingChanged(false);
      setFinalData(muteObject(data, { users: distributionListUsers }));
    },
    [data, recipientsIds, setFinalData, checkedState]
  );

  //callback used when single line CC Email checkbox is changed
  const handleToggleRecipientCCs = useCallback(
    id => {
      setNothingChanged(false);
      const index = selectedCCIds.indexOf(id);
      const distributionListUsers = [...data.users];
      const changedUserIndex = distributionListUsers.findIndex(
        user => `${user.userId}:${user.fundId}` === id
      );
      if (index === -1) {
        distributionListUsers[changedUserIndex] = muteObject(
          distributionListUsers[changedUserIndex],
          {
            isSelected: true,
            isSelectedCC: true
          }
        );
      } else {
        distributionListUsers[changedUserIndex] = muteObject(
          distributionListUsers[changedUserIndex],
          {
            isSelectedCC: false
          }
        );
      }
      checkedState.checked = calculateSelectedCCAll(distributionListUsers);
      setFinalData(muteObject(data, { users: distributionListUsers }));
    },
    [selectedCCIds, data, setFinalData, checkedState]
  );

  /**
   * When we click on the checkbox in a row  on excluded contacts dialog
   * @param {*} checked
   * @param {*} item
   */
  const handleRowSelect = useCallback((checked, item) => {
    if (checked) {
      setSelectedRows(selectedRows => selectedRows.concat(item));
    } else {
      setSelectedRows(selectedRows =>
        selectedRows.filter(itemRow => item !== itemRow)
      );
    }
  }, []);

  /**
   * Update recepients in distribution list
   */
  const handleSaveClick = async data => {
    //Prepare selected funds to be updated for curent distribution list
    const fundRecListToBeUpdated = [];
    data.users.forEach(recipient => {
      if (recipient.isSelected) {
        fundRecListToBeUpdated.push({
          fund: recipient.fundId,
          email: recipient.email,
          includeAllCcEmails: recipient.isSelectedCC
        });
      }
    });
    const payload = {
      name: record.name,
      emailTemplateId: record.emailTemplateId,
      emailFundRecipientList: fundRecListToBeUpdated
    };
    setSubmitting(true);
    try {
      await DistributionListApi.updateDistributionList(record.id, payload);
      notify('ra.notification.distribution_list_updated');
    } catch (error) {
      notifyServerError(error);
    } finally {
      setSubmitting(false);
      setNothingChanged(true);
      handleFilters([]);
      refresh();
    }
  };

  /**
   * Calculating number of rows depending of the filter
   */
  const numSelectedRows = useMemo(() => {
    let newArray = [];
    filteredExcludedContacts.forEach(item => {
      if (selectedRows.includes(item)) {
        newArray.push(item);
      }
    });
    return newArray.length;
  }, [filteredExcludedContacts, selectedRows]);

  const rowCount = useMemo(() => {
    return filteredExcludedContacts.length;
  }, [filteredExcludedContacts]);

  /**
   * When we click the global checkbox
   */
  const handleClickAll = useCallback(() => {
    if (numSelectedRows === rowCount) {
      const newArray = [];
      filteredExcludedContacts.forEach(item => {
        if (selectedRows.includes(item)) {
          newArray.push(item);
        }
      });
      setSelectedRows(difference(selectedRows, newArray));
    } else {
      setSelectedRows(selectedRows.concat(filteredExcludedContacts));
    }
  }, [numSelectedRows, filteredExcludedContacts, rowCount, selectedRows]);

  // Selection, EmailDialog style !!
  const selectedRecipients = [];
  if (selectedIds) {
    selectedIds.forEach(id => {
      const recipient = recipients[id];
      if (recipient) {
        selectedRecipients.push({
          userId: recipient.userId,
          fund: recipient.fundId,
          email: recipient.email,
          ccEmails: recipient.ccEmails,
          includeAllCcEmails: selectedCCIds.includes(id)
        });
      }
    });
  }

  const handleToggleAllRecipientCCs = useCallback(() => {
    const checked = !checkedState.checked;
    const newUsers = data.users.map(user => {
      if (user.isSelected && user.isSelectedCC !== checked) {
        return muteObject(user, { isSelectedCC: checked });
      } else {
        return user;
      }
    });
    checkedState.checked = checked;
    setFinalData(muteObject(data, { users: newUsers }));
  }, [data, setFinalData, checkedState]);

  useEffect(() => {
    setAddDisabled(selectedRows.length === 0);
  }, [selectedRows]);

  return (
    <>
      <Grid container spacing={1} justifyContent="center">
        <Grid item xs={12}>
          <Toolbar className={classes.toolbar}>
            <>
              <RecipientsFilters setFilters={handleFilters} />
              <span />
              <RecipientsActions
                classes={classes}
                selectedIds={selectedIds}
                onCompose={() => handleEmailDialogToggle(true)}
                onDownloadToExcel={() => handleClickDownloadAsExcel(record.id)}
                ids={ids}
                isLoading={isLoading}
                hasComposeEmailDLPermission={hasComposeEmailDLPermission}
                hasExportPermission={hasExportPermission}
                setExcludedContactsDialogOpen={setExcludedContactsDialogOpen}
                numberOfExcludedContacts={excludedContacts.length}
              />
            </>
          </Toolbar>
          <div className={classes.helperSearchInput}>
            You can input multiple keywords by separating them with comma
          </div>
        </Grid>
        <Grid item xs={12}>
          <CkAllContext.Provider value={{ ...checkedState }}>
            <Datagrid
              data={recipients}
              ids={recipientsIds}
              selectedIds={gridSelectedIds}
              resource="distributionlists"
              currentSort={{}}
              hasBulkActions
              onSelect={handleSelectRecipients}
              onToggleItem={handleToggleRecipient}
            >
              <TextField
                label="Email Address"
                source="email"
                sortable={false}
              />
              <FunctionField
                label={
                  <CkAllLabel
                    label="CC Email"
                    onChange={handleToggleAllRecipientCCs}
                  />
                }
                render={record => {
                  let label;
                  if (record.ccEmails.length === 0) {
                    label = null;
                  } else if (record.ccEmails.length === 1) {
                    label = record.ccEmails[0];
                  } else {
                    label = (
                      <PopupField
                        data={record.ccEmails}
                        label="Multiple CC Emails"
                        headLabel="CC Email"
                      />
                    );
                  }
                  return (
                    <>
                      {record.ccEmails.length > 0 && (
                        <Checkbox
                          checked={selectedCCIds.includes(
                            `${record.userId}:${record.fundId}`
                          )}
                          onClick={() =>
                            handleToggleRecipientCCs(
                              `${record.userId}:${record.fundId}`
                            )
                          }
                        />
                      )}
                      {label}
                    </>
                  );
                }}
              />
              <FunctionField
                label="Contact Name"
                render={record => `${record.firstName} ${record.lastName}`}
              />
              <TextField label="Fund Name" source="fundName" />
              <FunctionField
                label="Investor Name"
                render={record => {
                  if (record.investors.length === 0) {
                    return null;
                  } else if (record.investors.length === 1) {
                    return record.investors[0].name === 'ALL INVESTORS'
                      ? 'All Investors'
                      : record.investors[0].name;
                  } else {
                    return (
                      <PopupField
                        data={record.investors.map(investor => investor.name)}
                        label="Multiple Investors"
                        headLabel="Investors"
                      />
                    );
                  }
                }}
              />
            </Datagrid>
          </CkAllContext.Provider>
          {selectedIds.length < 1 && (
            <FormHelperText error>
              At least one recipient must be selected.
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12}>
          <div className={classes.actionBarContainer}>
            <div>
              <span className={classes.indicatorLabel}>
                Total Number of Contacts:
                {data && data.listInfo && (
                  <span
                    className={classes.indicatorNumber}
                  >{` ${data.listInfo.totalNumberOfContacts}`}</span>
                )}
              </span>
              <span className={classes.indicatorLabel}>
                Distinct Contacts:
                {data && data.listInfo && (
                  <span
                    className={classes.indicatorNumber}
                  >{` ${data.listInfo.distinctContacts}`}</span>
                )}
              </span>
              <span className={classes.indicatorLabel}>
                Non-Admin Contacts:
                {data && data.listInfo && (
                  <span
                    className={classes.indicatorNumber}
                  >{` ${data.listInfo.nonAdminContacts}`}</span>
                )}
              </span>
            </div>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className={classes.leftWrapper}>
            <div>
              <SaveButton
                saving={submitting}
                handleSubmitWithRedirect={() => handleSaveClick(data)}
                color="primary"
                disabled={
                  notChanged ||
                  submitting ||
                  (selectedIds && selectedIds.length < 1)
                }
                variant="contained"
                className={classes.button}
                label={'Save'}
                icon={<></>}
              />
              <MUIButton
                onClick={() => handleClose()}
                variant="outlined"
                color="primary"
                className={classes.composeEmailButton}
              >
                Close
              </MUIButton>
            </div>
          </div>
        </Grid>
      </Grid>
      <EmailDialog
        showDialog={showEmaildDialog}
        handleClose={() => handleEmailDialogToggle(false)}
        choices={data ? data.users : []}
        recipients={selectedRecipients}
        templateId={record.emailTemplateId}
        distributionListId={record.id}
        distributionListName={record.name}
        userProfile={userProfile}
      />
      <Dialog
        fullWidth
        open={excludedContactsDialogOpen}
        aria-label={`Add Excluded Contacts`}
        maxWidth={'lg'}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return null;
          setExcludedContactsDialogOpen(false);
        }}
      >
        <DialogTitleWithCloseButton
          onClose={() => setExcludedContactsDialogOpen(false)}
        >
          {'Add Excluded Contacts'}
        </DialogTitleWithCloseButton>
        <DialogContent>
          <span className={classes.indicatorLabel}>
            These Contacts are excluded from Distribution List.
            <br />
            You can add them back, by selecting Contacts you want, and clicking
            the Add button.
          </span>
          <MUITextField
            variant="standard"
            placeholder="Search"
            helperText={false}
            onChange={event => filterExcludedContacts(event)}
            className={classes.searchTextInput}
          />

          <Paper className={classes.paperContainer}>
            <div className={classes.table}>
              <Table
                stickyHeader
                aria-label="sticky table"
                size="small"
                className={classes.tableContainer}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      className={classes.checkboxCell}
                    >
                      <Checkbox
                        indeterminate={
                          numSelectedRows > 0 && numSelectedRows < rowCount
                        }
                        checked={numSelectedRows === rowCount && rowCount > 0}
                        onClick={handleClickAll}
                        className={classes.checkbox}
                      />
                    </TableCell>
                    {[
                      { id: 'Contact Name' },
                      { id: 'Email' },
                      { id: 'Fund Name' },
                      { id: 'Fund ID' }
                    ].map(headCell => (
                      <TableCell className={classes.cell} key={headCell.id}>
                        {headCell.id}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredExcludedContacts.map((item, index) => {
                    return (
                      <TableRow key={index} hover>
                        <TableCell
                          padding="checkbox"
                          className={classes.checkboxCell}
                        >
                          <Checkbox
                            onClick={event =>
                              handleRowSelect(event.target.checked, item)
                            }
                            className={item.checkbox}
                            checked={selectedRows.includes(item)}
                          />
                        </TableCell>
                        <TableCell>
                          {item.firstName + ' ' + item.lastName}
                        </TableCell>
                        <TableCell>{item.email}</TableCell>
                        <TableCell>{item.fundName}</TableCell>
                        <TableCell>{item.fundId}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Paper>
        </DialogContent>
        <DialogActions>
          <SaveButton
            handleSubmitWithRedirect={() => {
              addExcludedContacts(selectedRows);
              setSelectedRows([]);
              let newExcludedContacts = [...excludedContacts];
              newExcludedContacts = newExcludedContacts.filter(
                item => !selectedRows.includes(item)
              );
              setExcludedContacts(newExcludedContacts);
              setFilteredExcludedContacts(newExcludedContacts);
              setExcludedContactsDialogOpen(false);
            }}
            disabled={addDisabled}
            label={'Add'}
            icon={<></>}
          />
          <Button
            onClick={() => setExcludedContactsDialogOpen(false)}
            variant="outlined"
            color="primary"
            label="Cancel"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * List of recipients
 */
const Recipients = ({ record, classes, permissions = [], ...rest }) => {
  // Filters
  const [filters, setFilters] = useState({});
  //distribution list data with users and list info fetched from service
  const [finalData, setFinalData] = useState({});
  // DL recipients visible in grid after applying filters
  const [finalRecipients, setFinalRecipients] = useState({});

  const { hasComposeEmailDLPermission, hasExportPermission } = useMemo(() => {
    return {
      hasComposeEmailDLPermission: permissions.includes(
        'AUTH_COMPOSE_EMAIL_DL'
      ),
      hasExportPermission: permissions.includes('AUTH_EXPORT_EXCEL')
    };
  }, [permissions]);

  // Render after querying the recipients
  const { data, loading, error } = useQuery({
    type: 'getOne',
    resource: 'distributionlistrecipients',
    payload: {
      id: record.id,
      target: 'id'
    }
  });

  //effect used to set data about distribution list in state
  useEffect(() => {
    if (data && data.users) {
      const gridDataUsers = data.users.map(user => ({
        ...user,
        isSelected: true,
        isSelectedCC: Array.isArray(user.ccEmails) && user.ccEmails.length > 0
      }));
      const sortedUsers = sortBy(gridDataUsers, ['fundName', 'firstName']);
      setFinalData({ ...data, users: sortedUsers });
    }
  }, [data]);

  //list of all composite ids (userId:fundId pair) of all list recipients
  const ids = useMemo(() => {
    const newIds = [];
    if (finalData.users) {
      finalData.users.forEach(user => {
        const id = `${user.userId}:${user.fundId}`;
        newIds.push(id);
      });
    }
    return newIds;
  }, [finalData]);

  //list of all composite ids (userId:fundId pair) of all list recipients that has cc emails
  const ccIds = useMemo(() => {
    const newccIds = [];
    if (finalData.users) {
      finalData.users.forEach(user => {
        if (Array.isArray(user.ccEmails) && user.ccEmails.length > 0) {
          const id = `${user.userId}:${user.fundId}`;
          newccIds.push(id);
        }
      });
    }
    return newccIds;
  }, [finalData]);

  useEffect(() => {
    // Get filtered recipients
    // Mandatory input for Datagrid as data
    const recipients = {};
    if (finalData && finalData.users) {
      const contactList = filters.contact ? filters.contact.split(',') : [''];
      const emailList = filters.email ? filters.email.split(',') : [''];
      const investorList = filters.investors
        ? filters.investors.split(',')
        : [''];
      const fundList = filters.fundName ? filters.fundName.split(',') : [''];
      // filter by each user and check for email, names, funds, investors...
      finalData.users.forEach(user => {
        if (
          // filter by email address
          (!filters.email ||
            emailList.some(emailFilterValue =>
              user.email
                .toLowerCase()
                .includes(
                  emailFilterValue.trim() === ''
                    ? null
                    : emailFilterValue.toLowerCase().trim()
                )
            )) &&
          // filter by contact name (first name && last name toogether)
          (!filters.contact ||
            contactList.some(contactFilterValue =>
              (user.firstName + ' ' + user.lastName)
                .toLowerCase()
                .includes(
                  contactFilterValue.trim() === ''
                    ? null
                    : contactFilterValue.toLowerCase().trim()
                )
            )) &&
          // filter by fund name
          (!filters.fundName ||
            fundList.some(fundFilterValue =>
              user.fundName
                .toLowerCase()
                .includes(
                  fundFilterValue.trim() === ''
                    ? null
                    : fundFilterValue.toLowerCase().trim()
                )
            )) &&
          // filter by investor name
          (!filters.investors ||
            investorList.some(investorFilterValue =>
              user.investors.some(investor =>
                investor.name
                  .toLowerCase()
                  .includes(
                    investorFilterValue.trim() === ''
                      ? null
                      : investorFilterValue.toLowerCase().trim()
                  )
              )
            ))
        ) {
          const id = `${user.userId}:${user.fundId}`;
          recipients[id] = user;
        }
      });
      setFinalRecipients(recipients);
    }
  }, [finalData, filters]);

  //start filtering 500 ms after last character input in filter
  const handleFilters = debounce(values => {
    setFilters(values);
  }, 500);
  if (loading) return <Loading className={classes.loading} />;
  if (error) return <>error</>;
  if (
    finalData &&
    (!Array.isArray(finalData.users) || finalData.users.length < 1)
  )
    return <Typography>No Recipients to display</Typography>;

  return (
    <RecipientsGrid
      data={finalData}
      setFinalData={setFinalData}
      ids={ids}
      ccIds={ccIds}
      recipients={finalRecipients}
      handleFilters={handleFilters}
      classes={classes}
      record={record}
      hasComposeEmailDLPermission={hasComposeEmailDLPermission}
      hasExportPermission={hasExportPermission}
      {...rest}
    />
  );
};

/**
 * Read-only screen to present the distribution list recipients
 */
const DistributionListShow = ({
  classes,
  userProfile,
  hasDeletePermission,
  hasEditPermission,
  hasCreatePermission,
  ...rest
}) => (
  <Show title={<DistributionListTitle />} className={classes.root} {...rest}>
    <Recipients classes={classes} userProfile={userProfile} {...rest} />
  </Show>
);

const muteObject = (data, newData) => ({ ...data, ...newData });

export default withStyles(styles)(DistributionListShow);
